import { metaLines } from "./export.js";
import { westernDigits } from "./manifest.js";
import { paperOf, twips } from "./paper.js";
import { zip } from "./zip.js";
export const DOCX_TYPE = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
const W = 'http://schemas.openxmlformats.org/wordprocessingml/2006/main';
const R = 'http://schemas.openxmlformats.org/officeDocument/2006/relationships';
const XML = '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>\n';
/** Text safe inside XML: escaped, without the control characters XML 1.0 cannot carry, digits Western. */
export function docxText(v) {
    // eslint-disable-next-line no-control-regex
    return westernDigits(v).replace(/[\u0000-\u0008\u000B\u000C\u000E-\u001F￾￿]/g, '')
        .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}
/** Whether a text holds a right-to-left letter (Hebrew, Arabic and their supplements). */
function hasRtl(v) {
    return /[֐-ࣿיִ-﷿ﹰ-﻿]/.test(v);
}
const NUMERIC = new Set(['number', 'money', 'count']);
// The tones on paper: the print view's ink and a tenth of it behind (export-print.ts), without the #.
const TONE = {
    green: { ink: '166534', ground: 'DCF5E6' },
    amber: { ink: '92400E', ground: 'FDF0D5' },
    red: { ink: '991B1B', ground: 'FBE0E0' },
    blue: { ink: '1E40AF', ground: 'DDEBFB' },
    violet: { ink: '5B21B6', ground: 'EDE6FB' },
    neutral: { ink: '374151', ground: 'EEF0F3' },
};
// The print view's margins (export-print.ts `@page`): 12mm top, 10mm at the sides, 14mm below.
const MARGIN = { top: 12, side: 10, bottom: 14 };
/** One run: the text, its weight and size, and `rtl` when it holds Arabic. */
function run({ text, bold, size, color }) {
    const props = [
        bold ? '<w:b/><w:bCs/>' : '',
        color ? `<w:color w:val="${color}"/>` : '',
        size ? `<w:sz w:val="${size}"/><w:szCs w:val="${size}"/>` : '',
        hasRtl(text) ? '<w:rtl/>' : '',
    ].join('');
    return `<w:r>${props ? `<w:rPr>${props}</w:rPr>` : ''}<w:t xml:space="preserve">${docxText(text)}</w:t></w:r>`;
}
/** One paragraph, `bidi` in a right-to-left document, aligned to its end for a number. */
function paragraph(runs, opts) {
    const props = [
        opts.style ? `<w:pStyle w:val="${opts.style}"/>` : '',
        opts.keep ? '<w:keepNext/>' : '',
        opts.rtl ? '<w:bidi/>' : '',
        opts.end ? '<w:jc w:val="end"/>' : '',
    ].join('');
    return `<w:p>${props ? `<w:pPr>${props}</w:pPr>` : ''}${runs.map(run).join('')}</w:p>`;
}
/** The body of the document: title, the lines about the rows, the table, and the section. */
export function documentXml(table, meta, ctx, paper) {
    const rtl = ctx.i18n.dir === 'rtl';
    const sheet = paperOf(paper, table.columns.length);
    const usable = twips(sheet.width_mm - 2 * MARGIN.side);
    const n = Math.max(1, table.columns.length);
    const col = Math.floor(usable / n);
    const lines = metaLines(meta, ctx);
    const out = [];
    out.push(paragraph([{ text: meta.title }], { rtl, style: 'Title' }));
    for (const [label, value] of lines) {
        out.push(paragraph([{ text: `${label}: `, bold: true }, { text: value }], { rtl, style: 'Meta' }));
    }
    const cell = (body, opts = {}) => `<w:tc><w:tcPr><w:tcW w:w="${col}" w:type="dxa"/>${opts.shade ? `<w:shd w:val="clear" w:color="auto" w:fill="${opts.shade}"/>` : ''}</w:tcPr>${body}</w:tc>`;
    const head = `<w:tr><w:trPr><w:tblHeader/><w:cantSplit/></w:trPr>${table.columns.map((c) => {
        const label = c.kind === 'money' && c.currency ? `${c.label} (${c.currency})` : c.label;
        return cell(paragraph([{ text: label, bold: true }], { rtl, style: 'Cell', end: NUMERIC.has(c.kind) }), { shade: 'F1F3F5' });
    }).join('')}</w:tr>`;
    const rows = table.rows.map((row) => `<w:tr><w:trPr><w:cantSplit/></w:trPr>${row.map((c, i) => {
        const kind = table.columns[i].kind;
        if (c.tone)
            return cell(paragraph([{ text: c.text, bold: true, color: TONE[c.tone].ink }], { rtl, style: 'Cell' }), { shade: TONE[c.tone].ground });
        return cell(paragraph([{ text: c.text }], { rtl, style: 'Cell', end: NUMERIC.has(kind) }));
    }).join('')}</w:tr>`);
    const empty = table.rows.length ? [] : [`<w:tr><w:tc><w:tcPr><w:tcW w:w="${col * n}" w:type="dxa"/>${n > 1 ? `<w:gridSpan w:val="${n}"/>` : ''}</w:tcPr>${paragraph([{ text: ctx.i18n.t('list.empty.title') }], { rtl, style: 'Cell' })}</w:tc></w:tr>`];
    const totals = table.totals ? [`<w:tr><w:trPr><w:cantSplit/></w:trPr>${table.totals.map((c, i) => cell(paragraph([{ text: c.text, bold: true }], { rtl, style: 'Cell', end: NUMERIC.has(table.columns[i].kind) }))).join('')}</w:tr>`] : [];
    out.push(`<w:tbl><w:tblPr><w:tblStyle w:val="ExportTable"/>${rtl ? '<w:bidiVisual/>' : ''}<w:tblW w:w="5000" w:type="pct"/>`
        + '<w:tblBorders><w:top w:val="single" w:sz="8" w:color="9AA0A6"/><w:bottom w:val="single" w:sz="4" w:color="E5E7EB"/><w:insideH w:val="single" w:sz="4" w:color="E5E7EB"/></w:tblBorders>'
        + '<w:tblLayout w:type="autofit"/><w:tblCellMar><w:left w:w="80" w:type="dxa"/><w:right w:w="80" w:type="dxa"/></w:tblCellMar></w:tblPr>'
        + `<w:tblGrid>${Array.from({ length: n }, () => `<w:gridCol w:w="${col}"/>`).join('')}</w:tblGrid>`
        + head + rows.join('') + empty.join('') + totals.join('') + '</w:tbl>');
    // A document may not end on a table: Word adds a paragraph if there is none, and says it repaired the file.
    out.push(paragraph([], { rtl }));
    const section = `<w:sectPr><w:footerReference w:type="default" r:id="rId3"/>`
        + `<w:pgSz w:w="${twips(sheet.width_mm)}" w:h="${twips(sheet.height_mm)}"${sheet.landscape ? ' w:orient="landscape"' : ''}/>`
        + `<w:pgMar w:top="${twips(MARGIN.top)}" w:right="${twips(MARGIN.side)}" w:bottom="${twips(MARGIN.bottom)}" w:left="${twips(MARGIN.side)}" w:header="340" w:footer="340" w:gutter="0"/>`
        + (rtl ? '<w:bidi/>' : '')
        + '</w:sectPr>';
    return `${XML}<w:document xmlns:w="${W}" xmlns:r="${R}"><w:body>${out.join('')}${section}</w:body></w:document>`;
}
/** The styles: a default face that has Arabic, the title, the lines under it, and a table cell. */
function stylesXml(ctx) {
    const lang = ctx.i18n.locale === 'ar' ? '<w:lang w:val="ar-SA" w:bidi="ar-SA"/>' : '<w:lang w:val="en-US" w:bidi="ar-SA"/>';
    const face = '<w:rFonts w:ascii="Arial" w:hAnsi="Arial" w:cs="Arial" w:eastAsia="Arial"/>';
    return `${XML}<w:styles xmlns:w="${W}">`
        + `<w:docDefaults><w:rPrDefault><w:rPr>${face}<w:sz w:val="19"/><w:szCs w:val="19"/>${lang}</w:rPr></w:rPrDefault>`
        + '<w:pPrDefault><w:pPr><w:spacing w:after="0" w:line="264" w:lineRule="auto"/></w:pPr></w:pPrDefault></w:docDefaults>'
        + '<w:style w:type="paragraph" w:default="1" w:styleId="Normal"><w:name w:val="Normal"/><w:qFormat/></w:style>'
        + '<w:style w:type="paragraph" w:styleId="Title"><w:name w:val="Title"/><w:basedOn w:val="Normal"/><w:next w:val="Normal"/><w:qFormat/>'
        + '<w:pPr><w:spacing w:after="120"/></w:pPr><w:rPr><w:b/><w:bCs/><w:color w:val="111827"/><w:sz w:val="30"/><w:szCs w:val="30"/></w:rPr></w:style>'
        + '<w:style w:type="paragraph" w:styleId="Meta"><w:name w:val="Export meta"/><w:basedOn w:val="Normal"/>'
        + '<w:pPr><w:spacing w:after="20"/></w:pPr><w:rPr><w:color w:val="374151"/><w:sz w:val="17"/><w:szCs w:val="17"/></w:rPr></w:style>'
        + '<w:style w:type="paragraph" w:styleId="Cell"><w:name w:val="Export cell"/><w:basedOn w:val="Normal"/>'
        + '<w:pPr><w:spacing w:before="40" w:after="40"/></w:pPr></w:style>'
        + '<w:style w:type="table" w:default="1" w:styleId="TableNormal"><w:name w:val="Normal Table"/><w:tblPr><w:tblInd w:w="0" w:type="dxa"/>'
        + '<w:tblCellMar><w:top w:w="0" w:type="dxa"/><w:left w:w="108" w:type="dxa"/><w:bottom w:w="0" w:type="dxa"/><w:right w:w="108" w:type="dxa"/></w:tblCellMar></w:tblPr></w:style>'
        + '<w:style w:type="table" w:styleId="ExportTable"><w:name w:val="Export table"/><w:basedOn w:val="TableNormal"/></w:style>'
        + '<w:style w:type="paragraph" w:styleId="Footer"><w:name w:val="footer"/><w:basedOn w:val="Normal"/><w:pPr><w:jc w:val="center"/></w:pPr>'
        + '<w:rPr><w:color w:val="5F6368"/><w:sz w:val="17"/><w:szCs w:val="17"/></w:rPr></w:style>'
        + '</w:styles>';
}
/** The footer: «page / pages», the print view's page number, as Word fields. */
function footerXml(rtl) {
    const field = (code) => `<w:r><w:fldChar w:fldCharType="begin"/></w:r><w:r><w:instrText xml:space="preserve"> ${code} </w:instrText></w:r><w:r><w:fldChar w:fldCharType="separate"/></w:r><w:r><w:t>1</w:t></w:r><w:r><w:fldChar w:fldCharType="end"/></w:r>`;
    return `${XML}<w:ftr xmlns:w="${W}" xmlns:r="${R}"><w:p><w:pPr><w:pStyle w:val="Footer"/>${rtl ? '<w:bidi/>' : ''}</w:pPr>`
        + `${field('PAGE \\* Arabic')}<w:r><w:t xml:space="preserve"> / </w:t></w:r>${field('NUMPAGES \\* Arabic')}</w:p></w:ftr>`;
}
/** The document's parts, as text, by their path inside the zip. */
export function documentParts(table, meta, ctx, paper) {
    const at = new Date(meta.at);
    return {
        '[Content_Types].xml': `${XML}<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">`
            + '<Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>'
            + '<Default Extension="xml" ContentType="application/xml"/>'
            + '<Override PartName="/word/document.xml" ContentType="application/vnd.openxmlformats-officedocument.wordprocessingml.document.main+xml"/>'
            + '<Override PartName="/word/styles.xml" ContentType="application/vnd.openxmlformats-officedocument.wordprocessingml.styles+xml"/>'
            + '<Override PartName="/word/footer1.xml" ContentType="application/vnd.openxmlformats-officedocument.wordprocessingml.footer+xml"/>'
            + '<Override PartName="/docProps/core.xml" ContentType="application/vnd.openxmlformats-package.core-properties+xml"/>'
            + '</Types>',
        '_rels/.rels': `${XML}<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">`
            + '<Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="word/document.xml"/>'
            + '<Relationship Id="rId2" Type="http://schemas.openxmlformats.org/package/2006/relationships/metadata/core-properties" Target="docProps/core.xml"/>'
            + '</Relationships>',
        'docProps/core.xml': `${XML}<cp:coreProperties xmlns:cp="http://schemas.openxmlformats.org/package/2006/metadata/core-properties" xmlns:dc="http://purl.org/dc/elements/1.1/" xmlns:dcterms="http://purl.org/dc/terms/" xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance">`
            + `<dc:title>${docxText(meta.title)}</dc:title>`
            + (meta.by ? `<dc:creator>${docxText(meta.by)}</dc:creator>` : '')
            + `<dcterms:created xsi:type="dcterms:W3CDTF">${(Number.isNaN(at.getTime()) ? new Date() : at).toISOString().replace(/\.\d{3}Z$/, 'Z')}</dcterms:created>`
            + '</cp:coreProperties>',
        'word/_rels/document.xml.rels': `${XML}<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">`
            + '<Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/styles" Target="styles.xml"/>'
            + '<Relationship Id="rId3" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/footer" Target="footer1.xml"/>'
            + '</Relationships>',
        'word/document.xml': documentXml(table, meta, ctx, paper),
        'word/styles.xml': stylesXml(ctx),
        'word/footer1.xml': footerXml(ctx.i18n.dir === 'rtl'),
    };
}
/** The .docx file's bytes. */
export async function wordDocument(table, meta, ctx, options = {}) {
    const encoder = new TextEncoder();
    const parts = Object.entries(documentParts(table, meta, ctx, options.paper)).map(([name, text]) => ({ name, data: encoder.encode(text) }));
    const at = new Date(meta.at);
    return zip(parts, Number.isNaN(at.getTime()) ? new Date() : at, options.compress === undefined ? {} : { compress: options.compress });
}
//# sourceMappingURL=export-docx.js.map